import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { setUser, setLoading } from './store/slices/authSlice';
import { setShowIntroModal } from './store/slices/uiSlice';
import { setAnimals } from './store/slices/gameSlice';
import { authService } from './services/auth';
import AuthContainer from './components/auth/AuthContainer';
import InactiveInstructorScreen from './components/auth/InactiveInstructorScreen';
import IntroModal from './components/intro/IntroModal';
import PersonalProfile from './components/profile/PersonalProfile';
import Loading from './components/common/Loading';
import ForestView from './components/forest/ForestView';
import TeacherDashboard from './components/teacher/TeacherDashboard';
import { getAnimals, initializeDefaultData } from './services/firestore';
import { activityManager } from './services/activityManager';
import { perForestMonitor } from './services/perForestMonitor';
import { signOut } from 'firebase/auth';
import { auth } from './services/firebase';
import { installFirestoreDebug } from './services/firestoreDebug';
import { useFirestoreDebugger } from './services/useFirestoreDebugger';


const FSDEBUG_ENABLED =
  /(?:^|[?&])fsdebug=1(?:&|$)/.test(window.location.search) ||
  localStorage.getItem('FS_DEBUG') === '1';


function App() {
  useFirestoreDebugger();
  const dispatch = useDispatch();
  const { user, isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);
  const { showIntroModal } = useSelector((state: RootState) => state.ui);
  const [currentView, setCurrentView] = useState<'profile' | 'forest' | 'teacher'>('profile');

  useEffect(() => {
    console.log('Starting Hope Garden App...');

    if (FSDEBUG_ENABLED) {
      import('./services/firestoreDebug').then(m => m.installFirestoreDebug());
    }

    console.log('Firebase config check:', {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? 'Set' : 'Missing',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ? 'Set' : 'Missing',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ? 'Set' : 'Missing',
    });
    
    const initializeApp = async () => {
      console.log('initializeApp() called at:', new Date().toISOString());
      try {
        const animals = await getAnimals();

        if (animals.length === 0) {
          console.log('No animals found, initializing data...');
          await initializeDefaultData();
      
          const newAnimals = await getAnimals();
          console.log('Animals loaded (post init):', newAnimals.length);
          dispatch(setAnimals(newAnimals));
        } else {
          console.log('Using existing animals from database');
          dispatch(setAnimals(animals));
        }
      } catch (error) {
        console.error('ERROR: Failed to initialize app:', error);
      }
    };

    initializeApp();

    activityManager.startMonitoring();
    console.log('Activity-based monitoring started');

    const unsubscribe = authService.onAuthStateChange(async (user) => {
      console.log('INFO: Auth state changed:', user ? `User ${user.username}` : 'No user');
      dispatch(setUser(user));
      
      if (user) {
        activityManager.recordActivity('login');
        if (user?.showIntroduction) {
          dispatch(setShowIntroModal(true));
        }
      } else {
        activityManager.stopMonitoring();
        perForestMonitor.stopAllMonitoring();
      }
      
      dispatch(setLoading(false));
    });

    return () => {
      unsubscribe();
      activityManager.stopMonitoring();
      perForestMonitor.stopAllMonitoring();
      console.log('Activity monitoring stopped');
    };
  }, []);


  useEffect(() => {
  if (!FSDEBUG_ENABLED) return;
  //@ts-ignore
  if ((window as any).fsnet) return;

  const minuteWindow = new Map<string, number[]>(); //key -> timestamps
  const now = () => Date.now();
  const prune = (arr: number[]) => {
    const t = now();
    while (arr.length && t - arr[0] > 60_000) arr.shift();
  };
  const tick = (key: string) => {
    const arr = minuteWindow.get(key) ?? [];
    arr.push(now());
    prune(arr);
    minuteWindow.set(key, arr);
  };

  const parseBody = async (body: any): Promise<any | null> => {
    try {
      if (typeof body === 'string') return JSON.parse(body);
      if (body instanceof Blob) return JSON.parse(await body.text());
      if (body && typeof body === 'object' && 'text' in body) {
        //@ts-ignore
        return JSON.parse(await body.text());
      }
    } catch {}
    return null;
  };

  const deriveKeyFromFetch = (url: string, bodyJson: any) => {
    let op =
      url.includes('/Listen') ? 'Listen' :
      url.includes('/RunQuery') ? 'RunQuery' :
      url.includes('/BatchGetDocuments') ? 'BatchGetDocuments' :
      url.includes('/GetDocument') ? 'GetDocument' :
      'FirestoreAPI';

    let parent = bodyJson?.parent;
    let from =
      bodyJson?.structuredQuery?.from?.map((f: any) => f.collectionId).join(',') ||
      bodyJson?.addTarget?.query?.from?.map((f: any) => f.collectionId).join(',');

    if (parent || from) op += ` | parent:${parent ?? ''} from:${from ?? ''}`;
    return op;
  };

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : (input as Request).url;
    if (url.includes('firestore.googleapis.com')) {
      const bodyJson = await parseBody(init?.body);
      const key = deriveKeyFromFetch(url, bodyJson);
      tick(key);
    }
    return origFetch(input as any, init);
  };

  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  const xhrUrl = new WeakMap<XMLHttpRequest, string>();
  XMLHttpRequest.prototype.open = function (method: string, url: string, ...rest: any[]) {
    xhrUrl.set(this, url);
    // @ts-ignore
    return origOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (body?: any) {
    const url = xhrUrl.get(this) || '';
    if (url.includes('firestore.googleapis.com')) {

      let key = url.includes('/Listen') ? 'Listen' : 'XHR-Firestore';
      try {
        if (typeof body === 'string') {
          const j = JSON.parse(body);
          const parent = j?.parent ?? j?.database;
          const from =
            j?.addTarget?.query?.from?.map((f: any) => f.collectionId).join(',');
          if (parent || from) key += ` | parent:${parent ?? ''} from:${from ?? ''}`;
        }
      } catch {}
      tick(key);
    }
    //@ts-ignore
    return origSend.call(this, body);
  };

  // @ts-ignore
  (window as any).fsnet = {
    dump() {
      const rows = Array.from(minuteWindow.entries())
        .map(([k, ts]) => ({ source: k, readsLast60s: ts.length }))
        .filter(r => r.readsLast60s > 0)
        .sort((a, b) => b.readsLast60s - a.readsLast60s);
      console.table(rows);
      if (!rows.length) console.log('No Firestore reads in the last 60s.');
      return rows;
    },
    reset() {
      minuteWindow.clear();
      console.log('fsnet: counters reset');
    }
  };

  console.info('[fsnet] Firestore network tap installed. Try: fsnet.dump()');
}, []);


  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      
      if (hash === 'forest') {
        setCurrentView('forest');
      } else if (hash === 'teacher') {
        //Only instructors see teacher view
        if (user?.userType === 'instructor') {
          setCurrentView('teacher');
        } else {
          //Redirect non-instructors to profile
          window.location.hash = 'profile';
          setCurrentView('profile');
        }
      } else {
        setCurrentView('profile');
      }
    };

    //Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    //Check initial hash
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [user]);




  /*To Be Deleted ASAP*/


  useEffect(() => {
  import('./services/firestore').then(module => {
    (window as any).checkPhantomReads = module.checkAllActivePresence;
    console.log('Debug function loaded. Type: checkPhantomReads() in console');
  });
}, []);


/*
  useEffect(() => {
    
    //Check 30s
    const monitorInterval = setInterval(() => {
      console.log('=== ACTIVITY MONITOR ===');
      console.log('Time:', new Date().toLocaleTimeString());
      console.log('User active:', activityManager.isActive());
      console.log('Auth user:', auth.currentUser?.uid || 'None');
      console.log('Monitoring status:', perForestMonitor.getMonitoringStatus());
      
      //Debug
      if (typeof (window as any).debugFirestore === 'function') {
        (window as any).debugFirestore();
      }
    }, 30000);
    
    //Run to console
    (window as any).debugFirestore = async () => {
      const { debugFirestoreReads } = await import('./services/firestore');
      await debugFirestoreReads();
    };
    
    return () => clearInterval(monitorInterval);
  }, []);

  useEffect(() => {
    if (user?.uid === 'MSFxQOIEI7bphqeuQBcpSCCFbmG2' || user?.email === 'rolypoly@school.edu') {
      console.log('Force terminating problematic session');
      signOut(auth).then(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
      }).catch(() => {
        window.location.reload();
      });
    }
  }, [user]);



  useEffect(() => {
    console.log('Emergency interval cleanup');
    
    const highestIntervalId = setInterval(() => {}, 0) as unknown as number;
    clearInterval(highestIntervalId); 
    
    for (let i = 1; i < highestIntervalId; i++) {
      clearInterval(i);
      clearTimeout(i);
    }
    
    console.log(`Emergency cleanup: cleared intervals up to ID ${highestIntervalId}`);
  }, []);
*/

  /* END DELETE */



  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <AuthContainer />;
  }

  //Check for inactive instructors
  if (user?.userType === 'instructor' && !user?.isActive) {
    return <InactiveInstructorScreen />;
  }

  //Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'forest':
        return <ForestView />;
      case 'teacher':
        //Double-check(!) user type before rendering teacher dashboard
        return user?.userType === 'instructor' ? <TeacherDashboard /> : <PersonalProfile />;
      case 'profile':
      default:
        return <PersonalProfile />;
    }
  };

  return (
    <div className="min-h-screen bg-powder-50">
      {showIntroModal && user?.showIntroduction && <IntroModal />}
      {(!showIntroModal || !user?.showIntroduction) && renderCurrentView()}
    </div>
  );
}

export default App;